import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { forceCheck } from "react-lazyload";
import _throttle from "lodash/throttle";
import withCustomStyles from "./GRail.style";
import { scrollBehavior } from "../../utils/HelperUtils";

const Arrow = ({ dir }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {dir === "left" ? (
      <>
        <path d="M19 12H5" />
        <path d="m11 18-6-6 6-6" />
      </>
    ) : (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    )}
  </svg>
);

// Section with a title, optional "See all" and a horizontally scrolling,
// scroll-snapping row. `kind` sets the column width:
// product: 8 per row at 1320, 9 at ≥1720 (min 168) · brand: min 140 · banner: 3 per row
// (min 300) · category: fixed 112px.
class GRail extends React.Component {
  ownRef = React.createRef();

  // react-lazyload only re-checks on window scroll; cards revealed by
  // scrolling the rail sideways must be re-checked explicitly.
  handleRowScroll = _throttle(() => forceCheck(), 200);

  componentDidMount() {
    // first paint: cards already in view should load without a page scroll
    this.checkTimer = setTimeout(forceCheck, 0);
  }

  componentWillUnmount() {
    clearTimeout(this.checkTimer);
    this.handleRowScroll.cancel();
  }

  get scroller() {
    const { scrollerRef } = this.props;
    return (scrollerRef && scrollerRef.current) || this.ownRef.current;
  }

  scroll = dir => {
    const el = this.scroller;
    if (el) {
      el.scrollBy({
        left: dir * el.clientWidth * 0.8,
        behavior: scrollBehavior()
      });
    }
  };

  setRef = node => {
    this.ownRef.current = node;
    const { scrollerRef } = this.props;
    if (typeof scrollerRef === "function") scrollerRef(node);
    else if (scrollerRef) scrollerRef.current = node;
  };

  render() {
    const {
      classes,
      className,
      rowClassName,
      title,
      titleTag: TitleTag,
      seeAllTo,
      seeAllLabel,
      arrows,
      kind,
      ariaLabel,
      children
    } = this.props;

    return (
      <section
        className={classnames(classes.section, className)}
        aria-label={title ? undefined : ariaLabel}
      >
        {(title || seeAllTo || arrows) && (
          <div className={classes.head}>
            {title && <TitleTag className={classes.title}>{title}</TitleTag>}
            <div className={classes.headActions}>
              {seeAllTo && (
                <Link to={seeAllTo} className={classes.seeAll}>
                  {seeAllLabel}
                </Link>
              )}
              {arrows && (
                <>
                  <button
                    type="button"
                    className={classes.arrow}
                    onClick={() => this.scroll(-1)}
                    aria-label="Scroll left"
                  >
                    <Arrow dir="left" />
                  </button>
                  <button
                    type="button"
                    className={classes.arrow}
                    onClick={() => this.scroll(1)}
                    aria-label="Scroll right"
                  >
                    <Arrow dir="right" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        <div
          ref={this.setRef}
          className={classnames(classes.row, classes[kind], rowClassName)}
          onScroll={this.handleRowScroll}
        >
          {children}
        </div>
      </section>
    );
  }
}

GRail.propTypes = {
  title: PropTypes.node,
  titleTag: PropTypes.string,
  seeAllTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  seeAllLabel: PropTypes.node,
  arrows: PropTypes.bool,
  kind: PropTypes.oneOf([
    "product",
    "brand",
    "banner",
    "bannerWide",
    "category"
  ]),
  scrollerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  rowClassName: PropTypes.string,
  ariaLabel: PropTypes.string
};

GRail.defaultProps = {
  titleTag: "h2",
  seeAllLabel: "See all",
  arrows: false,
  kind: "product"
};

export default withCustomStyles(GRail);
